import { useState } from 'react';
import { ChevronDown, ChevronRight, CornerDownRight, X, RotateCcw } from 'lucide-react';
import type { IdentifierCluster, IdentifierVariant } from '@/types/profiler';
import { SEED_LABELS } from '@/types/profiler';
import { ConfidenceBar } from './ConfidenceBar';

const SEED_COLORS: Record<string, string> = {
  NAME: '#00C8FF', EMAIL: '#2979FF', PHONE: '#00C853', ADDRESS: '#F0B429',
  COMPANY: '#F0B429', PASSPORT: '#FF6B1A', RELATION: '#C026D3',
  USERNAME: '#00C8FF', PAN: '#00C853', VOTER_ID: '#00C853',
  DL: '#00C853', DOB: '#5A7A9A',
};

const SOURCE_BADGE: Record<string, { label: string; color: string }> = {
  breach_validated:          { label: 'VALIDATED', color: '#00C853' },
  'breach_pivot(validated)': { label: 'PIVOT·VALID', color: '#00C853' },
  'breach_pivot(unverified)':{ label: 'PIVOT·UNVERIFIED', color: '#F0B429' },
  breach:                    { label: 'BREACH·R1', color: '#5A7A9A' },
  input:                     { label: 'INPUT', color: '#00C8FF' },
  pan_verified:              { label: 'PAN·VERIFIED', color: '#00C853' },
  expanded:                  { label: 'EXPANDED', color: '#5A7A9A' },
  normalised:                { label: 'NORMALISED', color: '#5A7A9A' },
};

function sourceBadge(source: string) {
  const known = SOURCE_BADGE[source];
  if (known) return known;
  if (source.startsWith('breach(') && source.endsWith('src)')) {
    return { label: `BREACH·${source.slice(7, -1)}`, color: '#5A7A9A' };
  }
  return { label: source.toUpperCase().slice(0, 20), color: '#5A7A9A' };
}

function DbChips({ dbs }: { dbs: string[] }) {
  const [showAll, setShowAll] = useState(false);
  if (!dbs.length) return null;
  const visible = showAll ? dbs : dbs.slice(0, 3);
  const overflow = dbs.length - 3;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {visible.map((db, i) => (
        <span
          key={i}
          style={{
            background: '#0F1928',
            color: '#5A7A9A',
            border: '1px solid #182030',
            fontSize: 7,
            fontFamily: 'monospace',
            padding: '1px 5px',
            borderRadius: 4,
            letterSpacing: '0.05em',
          }}
        >
          {db}
        </span>
      ))}
      {!showAll && overflow > 0 && (
        <button
          onClick={e => { e.stopPropagation(); setShowAll(true); }}
          style={{
            background: '#0F1928',
            color: '#283A50',
            border: '1px solid #182030',
            fontSize: 7,
            fontFamily: 'monospace',
            padding: '1px 5px',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          +{overflow} more
        </button>
      )}
    </div>
  );
}

function VariantRow({
  v, color, isLast, discarded, onDiscard,
}: {
  v: IdentifierVariant; color: string; isLast: boolean;
  discarded: boolean; onDiscard: (value: string, undo: boolean) => void;
}) {
  const badge = sourceBadge(v.source);
  const dbs = v.breach_databases ?? [];

  return (
    <div
      className="group px-3 py-2"
      style={{
        borderBottom: isLast ? undefined : '1px solid #0F1928',
        opacity: discarded ? 0.45 : 1,
        background: discarded ? '#080C10' : undefined,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        {/* value + pivot indicator */}
        <div className="flex flex-col gap-0.5 min-w-0">
          {v.pivot_via && (
            <div className="flex items-center gap-1" style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace' }}>
              <CornerDownRight size={8} />
              <span>via {v.pivot_via}</span>
            </div>
          )}
          <span style={{
            color: discarded ? '#3A5A7A' : '#DCE8F5',
            fontFamily: 'monospace', fontSize: 10, wordBreak: 'break-all',
            textDecoration: discarded ? 'line-through' : undefined,
          }}>
            {v.value}
          </span>
          {discarded && (
            <span style={{ color: '#FF4444', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
              DISCARDED BY ANALYST
            </span>
          )}
          <DbChips dbs={dbs} />
        </div>

        {/* confidence + source badge + discard button */}
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          {!discarded && (
            <div style={{ width: 60 }}>
              <ConfidenceBar value={v.confidence} color={color} showLabel />
            </div>
          )}
          <div className="flex items-center gap-1">
            {!discarded && (
              <span
                style={{
                  background: `${badge.color}12`,
                  color: badge.color,
                  border: `1px solid ${badge.color}30`,
                  fontSize: 7, fontFamily: 'monospace', fontWeight: 700,
                  padding: '1px 5px', borderRadius: 99,
                  letterSpacing: '0.08em', whiteSpace: 'nowrap',
                }}
              >
                {badge.label}
              </span>
            )}
            <button
              onClick={() => onDiscard(v.value, discarded)}
              title={discarded ? 'Restore variant' : 'Discard — mark as irrelevant'}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              style={{
                background: discarded ? '#00C85318' : '#FF444418',
                border: `1px solid ${discarded ? '#00C85330' : '#FF444430'}`,
                color: discarded ? '#00C853' : '#FF4444',
                borderRadius: 4, padding: '2px 4px', cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
            >
              {discarded ? <RotateCcw size={8} /> : <X size={8} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TypeGroup({
  type, variants, discardedSet, onDiscard,
}: {
  type: string; variants: IdentifierVariant[];
  discardedSet: Set<string>; onDiscard: (value: string, undo: boolean) => void;
}) {
  const [open, setOpen] = useState(variants.length <= 3);
  const color = SEED_COLORS[type] ?? '#5A7A9A';
  const label = SEED_LABELS[type] ?? type;
  const discardedCount = variants.filter(v => discardedSet.has(v.value)).length;

  return (
    <div style={{ background: '#0C1118', border: `1px solid #182030`, borderRadius: 8, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span
            style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
            className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full tracking-wider"
          >
            {label}
          </span>
          <span style={{ color: '#5A7A9A' }} className="text-[10px] font-mono">
            {variants.length - discardedCount} active
            {discardedCount > 0 && (
              <span style={{ color: '#FF4444', marginLeft: 4 }}>{discardedCount} discarded</span>
            )}
          </span>
        </div>
        {open
          ? <ChevronDown size={12} style={{ color: '#5A7A9A' }} />
          : <ChevronRight size={12} style={{ color: '#5A7A9A' }} />}
      </button>

      {open && (
        <div style={{ borderTop: '1px solid #182030' }}>
          {variants.map((v, i) => (
            <VariantRow
              key={i} v={v} color={color} isLast={i === variants.length - 1}
              discarded={discardedSet.has(v.value)} onDiscard={onDiscard}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface Props {
  cluster: IdentifierCluster;
  expandable?: boolean;
  onDiscard?: (value: string, undo: boolean) => void;
}

export function IdentifierClusterGrid({ cluster, expandable: _expandable = true, onDiscard }: Props) {
  const discardedSet = new Set(cluster.discarded_variants ?? []);

  const grouped: Record<string, IdentifierVariant[]> = {};
  for (const v of cluster.variants) {
    (grouped[v.type] = grouped[v.type] || []).push(v);
  }

  const activeCount = cluster.variants.filter(v => !discardedSet.has(v.value)).length;
  const discardedTotal = cluster.variants.length - activeCount;

  const handleDiscard = (value: string, undo: boolean) => onDiscard?.(value, undo);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between mb-1">
        <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
          IDENTIFIER CLUSTER
        </span>
        <div className="flex items-center gap-3">
          {discardedTotal > 0 && (
            <span style={{ color: '#FF4444', fontSize: 9, fontFamily: 'monospace' }}>
              {discardedTotal} discarded
            </span>
          )}
          <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
            {activeCount} active
          </span>
        </div>
      </div>
      {Object.entries(grouped).map(([type, variants]) => (
        <TypeGroup
          key={type} type={type} variants={variants}
          discardedSet={discardedSet} onDiscard={handleDiscard}
        />
      ))}
    </div>
  );
}
